import React, { useRef, useState, useEffect } from "react";
import {
  GoogleMap,
  LoadScript,
  Polyline,
  Marker,
} from "@react-google-maps/api";
import { config } from "../config";
import ApiClient from "../services/fuelandmoreapi";
import type { TPortNote } from "@/types";
import "./Map.css";
import type {
  TBunkerPrice,
  TCurrentVessel,
  TPort,
  TWeather,
  TCrudePrice,
} from "@/types";
import ClusteredBunkerPrices from "./ClusteredBunkerPrices";
import { Dialog, DialogTitle, DialogContent } from "@mui/material";
import { formatUTC } from "../utils/date";
import BunkerPriceGrid from "./BunkerPriceGrid";

const center = {
  lat: 37.7749,
  lng: -122.4194,
};

const mapOptions = {
  streetViewControl: false,
  fullscreenControl: false,
  mapTypeControl: false,
};

// Small anchor icon for ports with circular highlight
function createPortAnchorIcon(color: string = "#000000"): google.maps.Icon {
  const size = 18;
  const bgStroke = "#000000";
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" fill="#ffffff" stroke="${bgStroke}" stroke-width="1.5" />
      <g fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="5" r="2" fill="${color}"/>
        <path d="M12 7v9"/>
        <path d="M7 16c1.5 3 4 5 5 5s3.5-2 5-5"/>
        <path d="M5 18h3M16 18h3"/>
      </g>
    </svg>
  `;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size / 2),
  };
}

interface MapProps {
  vessels: TCurrentVessel[];
  routeCoordinates: [number, number][][];
  routeColors: string[];
  bunkerMarkers: TPort[][];
  weather: Map<string, TWeather[]>;
  prices: TBunkerPrice[];
  crudePrice?: TCrudePrice; // Brent crude price
  distanceNm: number;
  onLoad: (map: google.maps.Map) => void;
  selectedPortCallDetails: {
    vessel: TCurrentVessel | null;
    fromPort: string;
    destinationPort: string;
    eta: string;
  } | null;
}

// Use shared UTC formatter from utils/date

type TooltipState = {
  visible: boolean;
  x: number;
  y: number;
  name: string;
  obsDate?: string;
};

const Map: React.FC<MapProps> = ({
  vessels,
  routeCoordinates,
  routeColors,
  bunkerMarkers,
  prices,
  crudePrice,
  onLoad,
  selectedPortCallDetails,
}) => {
  const [portNotes, setPortNotes] = useState<TPortNote[]>([]);
  const [portTooltip, setPortTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    name: string;
    notes: TPortNote[];
  }>({ visible: false, x: 0, y: 0, name: "", notes: [] });
  const mapRef = useRef<google.maps.Map | null>(null);
  const vesselsRef = useRef<TCurrentVessel[]>([]);
  const [zoom, setZoom] = useState<number>(5);
  const [isRoutePlotted, setIsRoutePlotted] = useState<boolean>(false);
  const [visibleRoutes, setVisibleRoutes] = useState<boolean[]>(() =>
    routeCoordinates.map((_, idx) => idx === 0)
  );
  const [featureFlags, setFeatureFlags] = useState<Record<string, any>>({});
  const apiClientRef = useRef<ApiClient | null>(null);
  const [showPriceGrid, setShowPriceGrid] = useState<boolean>(false);
  const [selectedPrice, setSelectedPrice] = useState<TBunkerPrice | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    name: "",
    obsDate: undefined,
  });

  useEffect(() => {
    // Fetch port notes on mount
    (async () => {
      try {
        const apiClient = new ApiClient(config.apiBaseUrl || "");
        const notes = await apiClient.getPortNotes();
        setPortNotes(notes);
      } catch (err) {
        setPortNotes([]);
      }
    })();
    apiClientRef.current = new ApiClient(config.apiBaseUrl || "");
    (async () => {
      try {
        const flags = await apiClientRef.current!.getFeatureFlags();
        setFeatureFlags(flags || {});
        try {
          localStorage.feature_flags = JSON.stringify(flags || {});
        } catch {}
      } catch {}
    })();
  }, []);

  useEffect(() => {
    setIsRoutePlotted(routeCoordinates.length > 0);
    setVisibleRoutes(routeCoordinates.map((_, idx) => idx === 0));
    if (routeCoordinates.length > 0) setShowPriceGrid(true);
    else setShowPriceGrid(false);
  }, [routeCoordinates]);

  // Hide grid automatically if no routes are currently selected
  useEffect(() => {
    if (!visibleRoutes.some((v) => v)) {
      setShowPriceGrid(false);
    }
  }, [visibleRoutes]);

  const handleZoomChanged = () => {
    if (mapRef.current) setZoom(mapRef.current.getZoom() ?? 5);
  };

  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    // Invoke parent onLoad if provided
    if (onLoad) onLoad(map);
  };

  const getPricesForPort = (unlocode: string) => {
    return prices.filter(
      (price) => price.bunkerId.toLowerCase() === unlocode.toLowerCase()
    );
  };

  // Compute visible ports from visible routes once and reuse
  const visiblePorts = React.useMemo(
    () =>
      bunkerMarkers
        .map((group, idx) => (visibleRoutes[idx] ? group : []))
        .flat(),
    [bunkerMarkers, visibleRoutes]
  );

  const getRiskColor = (score: number | null) => {
    if (score === null || score === undefined) return "";
    if (score > 70) return "red";
    if (score > 30) return "amber";
    return "green";
  };

  const handleDialogClose = () => setSelectedPrice(null);

  const handleVesselClick = (lat: number, lng: number) => {
    if (mapRef.current) {
      mapRef.current.panTo({ lat, lng });
      mapRef.current.setZoom(9);
    }
  };

  const handleVesselMouseOver = (
    e: google.maps.MapMouseEvent,
    vessel: TCurrentVessel
  ) => {
    if (!e.domEvent) return;
    setTooltip({
      visible: true,
      x: (e.domEvent as MouseEvent).clientX,
      y: (e.domEvent as MouseEvent).clientY,
      name: vessel.name,
      obsDate: vessel.observationDateTime
        ? formatUTC(vessel.observationDateTime, "datetime")
        : undefined,
    });
  };

  const handleVesselMouseOut = () =>
    setTooltip((t) => ({ ...t, visible: false }));

  function createVesselIcon(
    heading: number,
    color: string = "red"
  ): google.maps.Icon {
    const size = 10;
    const path = `M0,-${size} L${size / 2},${size / 2} L-${size / 2},${size / 2} Z`;

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size * 2}" height="${size * 2}" viewBox="${-size} ${-size} ${size * 2} ${size * 2}">
        <g transform="rotate(${heading})">
          <path d="${path}" fill="${color}" stroke="white" stroke-width="1" />
        </g>
      </svg>
    `;

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(size * 2, size * 2),
      anchor: new google.maps.Point(size, size),
    };
  }

  useEffect(() => {
    vesselsRef.current = vessels;
    if (mapRef.current && vessels.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      if (vessels.length === 1) {
        const vessel = vessels[0];
        if (vessel.latitude && vessel.longitude) {
          mapRef.current.panTo({ lat: vessel.latitude, lng: vessel.longitude });
          mapRef.current.setZoom(9);
        }
      } else {
        vessels.forEach((v) => {
          if (v.latitude && v.longitude)
            bounds.extend({ lat: v.latitude, lng: v.longitude });
        });
        mapRef.current.fitBounds(bounds);
      }
    }
  }, [vessels]);

  useEffect(() => {
    if (mapRef.current && routeCoordinates.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      routeCoordinates.forEach((route) =>
        route.forEach(([lat, lng]) => bounds.extend({ lat, lng }))
      );
      mapRef.current.fitBounds(bounds);
    }
  }, [routeCoordinates]);

  return (
    <div className="map-container" style={{ position: "relative" }}>
      {/* Route toggles - only show if routes are available */}
      {routeCoordinates.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 10,
            background: "rgba(255,255,255,0.9)",
            borderRadius: 8,
            padding: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            maxWidth: 200,
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: 4 }}>Routes</div>
          {routeCoordinates.map((_, idx) => (
            <label key={idx} style={{ display: "block", marginBottom: 2 }}>
              <input
                type="checkbox"
                checked={visibleRoutes[idx]}
                onChange={() =>
                  setVisibleRoutes((prev) =>
                    prev.map((v, i) => (i === idx ? !v : v))
                  )
                }
                style={{ marginRight: 4 }}
              />
              {`Route ${idx + 1}`}
            </label>
          ))}
          <button
            type="button"
            onClick={() => setShowPriceGrid((p) => !p)}
            style={{
              background: showPriceGrid ? "#1e3e57" : "#ffffff",
              color: showPriceGrid ? "#ffffff" : "#1e3e57",
              border: "1px solid #1e3e57",
              padding: "6px 10px",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: 600,
              width: "100%",
              marginTop: 6,
            }}
            title="Toggle bunker prices"
          >
            {showPriceGrid ? "Hide Prices" : "Show Prices"}
          </button>
        </div>
      )}
      <LoadScript googleMapsApiKey={config.googleMapsApiKey}>
        <GoogleMap
          mapContainerClassName="map"
          center={center}
          zoom={5}
          options={mapOptions}
          onLoad={handleMapLoad}
          onZoomChanged={handleZoomChanged}
        >
          {/* Plot only visible polylines */}
          {routeCoordinates.map((route, index) =>
            visibleRoutes[index] ? (
              <Polyline
                key={index}
                path={route.map(([lat, lng]) => ({ lat, lng }))}
                options={{
                  strokeColor: routeColors[index] || "#1e3e57",
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  icons: [
                    {
                      icon: {
                        path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        scale: 1,
                        strokeColor: routeColors[index] || "#1e3e57",
                      },
                      offset: "100%",
                      repeat: "50px",
                    },
                  ],
                }}
              />
            ) : null
          )}

          {/* Anchor markers at every visible port when feature flag enabled */}
          {featureFlags.MAP_SHOW_PORT_MARKERS &&
            visiblePorts.map((port, idx) => {
              const notesForPort = portNotes.filter(
                (n) =>
                  n.unlocode?.toLowerCase() === port.unlocode?.toLowerCase()
              );
              const markerColor =
                notesForPort.length > 0 ? "orange" : undefined;
              return (
                <Marker
                  key={`port-anchor-${port.unlocode}-${idx}`}
                  position={{ lat: port.latitude, lng: port.longitude }}
                  icon={createPortAnchorIcon(markerColor)}
                  title={`${port.name} (${port.unlocode.slice(0, 2).toUpperCase()})`}
                  options={{ zIndex: 1 }}
                  onMouseOver={(e) => {
                    if (!e.domEvent) return;
                    setPortTooltip({
                      visible: true,
                      x: (e.domEvent as MouseEvent).clientX,
                      y: (e.domEvent as MouseEvent).clientY,
                      name: port.name,
                      notes: notesForPort,
                    });
                  }}
                  onMouseOut={() =>
                    setPortTooltip((t) => ({ ...t, visible: false }))
                  }
                />
              );
            })}

          {/* Only show port markers, prices, and weather for visible routes */}
          <ClusteredBunkerPrices
            bunkerMarkers={visiblePorts}
            prices={prices}
            zoom={zoom}
            getPricesForPort={getPricesForPort}
            vessel={selectedPortCallDetails?.vessel || null}
            destinationPort={selectedPortCallDetails?.destinationPort || ""}
            fromPort={selectedPortCallDetails?.fromPort || ""}
            eta={selectedPortCallDetails?.eta || ""}
            crudePrice={crudePrice}
          />

          {vessels.map((vessel, i) => (
            <Marker
              key={i}
              position={{ lat: vessel.latitude, lng: vessel.longitude }}
              icon={createVesselIcon(
                vessel.heading,
                getRiskColor(vessel.riskScore)
              )}
              onClick={() =>
                handleVesselClick(vessel.latitude, vessel.longitude)
              }
              onMouseOver={(e) => handleVesselMouseOver(e, vessel)}
              onMouseOut={handleVesselMouseOut}
            />
          ))}
        </GoogleMap>
      </LoadScript>

      <Dialog open={!!selectedPrice} onClose={handleDialogClose}>
        <DialogTitle>Price Details</DialogTitle>
        <DialogContent>
          {selectedPrice && (
            <div>
              <p>
                <strong>Fuel Grade:</strong> {selectedPrice.fuelGrade}
              </p>
              <p>
                <strong>Price:</strong> ${selectedPrice.price}
              </p>
              <p>
                <strong>Source:</strong> {selectedPrice.source}
              </p>
              <p>
                <strong>Timestamp:</strong>{" "}
                {formatUTC(selectedPrice.timestamp, "datetime")}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {isRoutePlotted && crudePrice && (
        <div className="crude-price-box">
          <div>
            <strong>Brent Crude</strong>
          </div>
          <div>
            {(() => {
              const v = crudePrice.timestamp as unknown as
                | Date
                | string
                | number;
              return formatUTC(v, "datetime");
            })()}
          </div>
          <div>Current: ${crudePrice.close.toFixed(2)}</div>
          {crudePrice.open && <div>Open: ${crudePrice.open.toFixed(2)}</div>}
          <div>High: ${crudePrice.high.toFixed(2)}</div>
          <div>Low: ${crudePrice.low.toFixed(2)}</div>
          <div>Volume: {crudePrice.volume.toLocaleString()}</div>
        </div>
      )}

      {/* Vessel Tooltip */}
      {tooltip.visible && (
        <div
          className="vessel-tooltip"
          style={{
            left: tooltip.x + 12,
            top: tooltip.y + 12,
            position: "fixed",
          }}
        >
          <div>
            <strong>{tooltip.name}</strong>
          </div>
          {tooltip.obsDate && (
            <div style={{ fontSize: 12 }}>{tooltip.obsDate}</div>
          )}
        </div>
      )}
      {/* Port Tooltip */}
      {portTooltip.visible && (
        <div
          className="vessel-tooltip"
          style={{
            left: portTooltip.x + 12,
            top: portTooltip.y + 12,
            position: "fixed",
            background: "#fff8e1",
            border: "1px solid orange",
            color: "#1e3e57",
            minWidth: 180,
            zIndex: 1000,
          }}
        >
          <div>
            <strong>{portTooltip.name}</strong>
          </div>
          {portTooltip.notes.length > 0 ? (
            <div style={{ marginTop: 6 }}>
              <strong>Notes:</strong>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {portTooltip.notes.map((note, i) => (
                  <li key={i}>
                    {note.note}
                    {note.expires && (
                      <span
                        style={{ color: "#888", fontSize: 12, marginLeft: 8 }}
                      >
                        (Expires: {new Date(note.expires).toLocaleDateString()})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div style={{ marginTop: 6, color: "#888" }}>
              No notes for this port.
            </div>
          )}
        </div>
      )}
      {showPriceGrid &&
        routeCoordinates.length > 0 &&
        visibleRoutes.some((v) => v) && (
          <BunkerPriceGrid
            ports={visiblePorts}
            prices={prices}
            getPricesForPort={getPricesForPort}
            vessel={selectedPortCallDetails?.vessel || null}
            fromPort={selectedPortCallDetails?.fromPort || ""}
            toPort={selectedPortCallDetails?.destinationPort || ""}
            eta={selectedPortCallDetails?.eta || ""}
            onClose={() => setShowPriceGrid(false)}
          />
        )}
    </div>
  );
};

export default Map;
